import React, { useState } from 'react';
import { ExternalLink, Target, Database, Shield, BarChart3, CheckCircle, XCircle, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';

import { getRoomContent } from "./roomList";
import { useLanguage } from './hooks/useLanguage';
import LanguageSwitcher from './components/LanguageSwitcher';

import { THEME, A11Y_HELPERS } from './theme';

// Constants
const RESPONSE_DISPLAY_TIME = 1000;

/* ───────────── Boilerplate room object ───────────── */
// const roomTemplate: Room = {
//   /* machine‑readable id (use kebab‑ or camel‑case) */
//   id: 'yourRoomId',
//
//   /* hero image displayed for the room */
//   image: '/images/yourRoom.png',
//
//   /* all user‑facing strings grouped by language */
//   content: {
//     de: {
//       title: 'Deutscher Titel',
//       description: `Mehrzeiliger Beschreibungstext …`,
//       question: 'Was möchtest du tun?'
//     },
//     en: {
//       title: 'English title',
//       description: `Multi‑line description …`,
//       question: 'What would you like to do?'
//     }
//     // add more locales here when needed
//   },
//
//   /* array of possible player choices */
//   choices: [
//     {
//       text: {
//         de: 'Option in Deutsch',
//         en: 'Option in English'
//       },
//       response: {
//         de: 'Antwort in Deutsch …',
//         en: 'Response in English …'
//       },
//       /* id of the next room, or null if this choice ends the game */
//       nextRoom: 'idOfNextRoom',
//
//       /* optional metadata */
//       goodDecision: true,       // marks a “good” path for scoring or highlighting
//       ending: 'idOfEnding'      // shown only when nextRoom is null
//     }
//     // …add further choices as needed
//   ]
// };

export default function App() {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [currentRoom, setCurrentRoom] = useState('hauntedhouse');
  const [roomHistory, setRoomHistory] = useState(['hauntedhouse']); // Track room history
  const [showResponse, setShowResponse] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [gameEnded, setGameEnded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [endingType, setEndingType] = useState('neutral');

  const room = getRoomContent(currentRoom, currentLanguage);

  const handleChoice = (choice) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentResponse(choice.response);
    setShowResponse(true);

    // Determine ending type based on response
    if (choice.response.includes('Zielsetzung und Strategie') || choice.response.includes('goals and strategy')) {
      setEndingType('success');
    } else if (choice.response.includes('Datenverlust') || choice.response.includes('data loss') || 
               choice.response.includes('Vielleicht war es zu viel') || choice.response.includes('maybe it was too much')) {
      setEndingType('failure');
    } else if (choice.response.includes('Cost per Data Unit') || choice.response.includes('Erkenntnis') || 
               choice.response.includes('insight') || choice.response.includes('realization')) {
      setEndingType('awareness');
    }

    // Show response for 4 seconds, then transition
    setTimeout(() => {
      if (choice.nextRoom === null) {
        setGameEnded(true);
      } else {
        setCurrentRoom(choice.nextRoom);
        // Add new room to history if it's not already the current room
        setRoomHistory(prev => [...prev, choice.nextRoom]);
      }
      setShowResponse(false);
      setIsTransitioning(false);
    }, RESPONSE_DISPLAY_TIME);
  };

  const goBack = () => {
    if (roomHistory.length > 1 && !isTransitioning) {
      const newHistory = [...roomHistory];
      newHistory.pop(); // Remove current room
      const previousRoom = newHistory[newHistory.length - 1];
      setCurrentRoom(previousRoom);
      setRoomHistory(newHistory);
    }
  };

  const resetGame = () => {
    setCurrentRoom('hauntedhouse');
    setRoomHistory(['hauntedhouse']);
    setShowResponse(false);
    setCurrentResponse('');
    setGameEnded(false);
    setIsTransitioning(false);
    setEndingType('neutral');
  };

  const getEndingMessage = () => {
    const endingConfig = {
      success: {
        title: t.endings.success.title,
        icon: <CheckCircle className="w-16 h-16 text-green-400" />,
        message: t.endings.success.message,
        color: 'text-green-400',
        borderColor: 'border-green-400',
        bgAccent: 'bg-green-400/10',
        services: [
          {
            title: t.services.dataStrategy.title,
            description: t.services.dataStrategy.description,
            icon: <Target className="w-6 h-6" />,
            url: 'https://www.feld-m.de/services/data-strategy'
          },
          {
            title: t.services.dataArchitecture.title,
            description: t.services.dataArchitecture.description,
            icon: <Database className="w-6 h-6" />,
            url: 'https://www.feld-m.de/services/data-architecture'
          }
        ]
      },
      failure: {
        title: t.endings.failure.title,
        icon: <XCircle className="w-16 h-16 text-red-400" />,
        message: t.endings.failure.message,
        color: 'text-red-400',
        borderColor: 'border-red-400',
        bgAccent: 'bg-red-400/10',
        services: [
          {
            title: t.services.dataGovernance.title,
            description: t.services.dataGovernance.description,
            icon: <Shield className="w-6 h-6" />,
            url: 'https://www.feld-m.de/service/data-strategy-consulting/'
          },
          {
            title: t.services.dataEngineering.title,
            description: t.services.dataEngineering.description,
            icon: <CheckCircle className="w-6 h-6" />,
            url: 'https://www.feld-m.de/service/data-engineering-consulting/'
          }
        ]
      },
      awareness: {
        title: t.endings.awareness.title,
        icon: <AlertCircle className="w-16 h-16 text-amber-400" />,
        message: t.endings.awareness.message,
        color: 'text-amber-400',
        borderColor: 'border-amber-400',
        bgAccent: 'bg-amber-400/10',
        services: [
          {
            title: t.services.dataAnalytics.title,
            description: t.services.dataAnalytics.description,
            icon: <BarChart3 className="w-6 h-6" />,
            url: 'https://www.feld-m.de/services/data-analytics'
          },
          {
            title: t.services.dataAssessment.title,
            description: t.services.dataAssessment.description,
            icon: <Database className="w-6 h-6" />,
            url: 'https://www.feld-m.de/services/data-assessment'
          }
        ]
      },
      neutral: {
        title: t.endings.neutral.title,
        icon: <Database className="w-16 h-16 text-blue-400" />,
        message: t.endings.neutral.message,
        color: 'text-blue-400',
        borderColor: 'border-blue-400',
        bgAccent: 'bg-blue-400/10',
        services: [
          {
            title: t.services.dataStrategyConsulting.title,
            description: t.services.dataStrategyConsulting.description,
            icon: <Target className="w-6 h-6" />,
            url: 'https://www.feld-m.de/service/data-strategy-consulting/'
          },
          {
            title: t.services.dataArchitectureConsulting.title,
            description: t.services.dataArchitectureConsulting.description,
            icon: <Shield className="w-6 h-6" />,
            url: 'https://www.feld-m.de/service/data-engineering-consulting/'
          }
        ]
      }
    };
    
    return endingConfig[endingType] || endingConfig.neutral;
  };

  if (gameEnded) {
    const ending = getEndingMessage();
    return (
      <div className={`min-h-screen ${THEME.bg.primary} ${THEME.text.primary} p-6`}>
        <div className="max-w-4xl mx-auto">
          {/* Header with Icon */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {ending.icon}
            </div>
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${ending.color}`}>
              {ending.title}
            </h1>
          </div>

          {/* Main Message */}
          <div className={`${THEME.bg.secondary} ${ending.bgAccent} p-8 rounded-lg mb-8 border-2 ${ending.borderColor} shadow-2xl`}>
            <p className={`text-xl ${THEME.text.secondary} leading-relaxed text-center`}>
              {ending.message}
            </p>
          </div>

          {/* Next Steps Section */}
          {endingType === 'neutral' && (
            <div className={`${THEME.bg.secondary} p-6 rounded-lg mb-8 ${THEME.border.primary} border`}>
              <h3 className={`text-xl font-semibold mb-4 ${THEME.text.accent}`}>{t.nextSteps.title}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className={`${THEME.text.secondary}`}>
                  <strong>• {t.nextSteps.steps.prioritize}</strong>
                </div>
                <div className={`${THEME.text.secondary}`}>
                  <strong>• {t.nextSteps.steps.inventory}</strong>
                </div>
                <div className={`${THEME.text.secondary}`}>
                  <strong>• {t.nextSteps.steps.reduce}</strong>
                </div>
                <div className={`${THEME.text.secondary}`}>
                  <strong>• {t.nextSteps.steps.pilot}</strong>
                </div>
              </div>
            </div>
          )}

          {/* FELD M Services */}
          <div className={`${THEME.bg.secondary} p-6 rounded-lg mb-8 ${THEME.border.primary} border`}>
            <h3 className={`text-xl font-semibold mb-4 ${THEME.text.accent} text-center`}>
              {t.company.helpTitle}
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {ending.services.map((service, index) => (
                <div key={index} className={`${THEME.bg.tertiary} p-4 rounded-lg ${THEME.border.secondary} border hover:${THEME.border.hover} transition-all duration-200`}>
                  <div className="flex items-start space-x-3">
                    <div className={`${ending.color} mt-1`}>
                      {service.icon}
                    </div>
                    <div>
                      <h4 className={`font-semibold ${THEME.text.primary} mb-2`}>
                        {service.title}
                      </h4>
                      <p className={`${THEME.text.secondary} text-sm mb-3`}>
                        {service.description}
                      </p>
                      <a
                        href={service.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-1 ${ending.color} hover:underline text-sm font-medium`}
                      >
                        <span>{t.ui.learnMore}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Company Info */}
            <div className="text-center">
              <p className={`${THEME.text.secondary} text-sm mb-4`}>
                {t.company.description}
              </p>
              <a
                href="https://www.feld-m.de"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center space-x-2 ${ending.color} hover:underline font-semibold`}
              >
                <span>{t.ui.visitFeldM}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Reset Button */}
          <div className="text-center">
            <button
              onClick={resetGame}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t.ui.restartGame}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResponse) {
    return (
      <div className={`min-h-screen ${THEME.bg.primary} ${THEME.text.primary} flex items-center justify-center p-6`}>
        <div className="max-w-3xl mx-auto text-center">
          <div className={`${THEME.bg.secondary} p-8 rounded-lg ${THEME.border.primary} border shadow-2xl`}>
            <p className={`text-lg ${THEME.text.secondary} leading-relaxed mb-6`}>
              {currentResponse}
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className={`animate-spin h-4 w-4 border-2 border-blue-400 rounded-full border-t-transparent`}></div>
              <div className={`${THEME.text.muted}`}>
                {t.ui.loadingNextRoom}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${THEME.bg.primary} ${THEME.text.primary} p-4 md:p-6`}>
      <div className="max-w-5xl mx-auto">
        <header className="text-center mb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1"></div>
            <div className="flex-1">
              <h1 className={`text-3xl md:text-4xl font-bold ${THEME.text.accent} mb-2`}>
                {t.ui.title}
              </h1>
            </div>
            <div className="flex-1 flex justify-end">
              <LanguageSwitcher
                currentLanguage={currentLanguage}
                onLanguageChange={changeLanguage}
                theme={THEME}
              />
            </div>
          </div>
          <p className={`${THEME.text.muted} mb-2`}>
            {t.ui.subtitle}
          </p>
          <p className={`${THEME.text.secondary} text-sm`}>
            {t.ui.blogReference}{' '}
            <a
              href="https://www.feld-m.de/blog/dark-data-schlechte-datenqualitaet-kostet/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-blue-400 hover:text-blue-300"
            >
              {t.ui.blogLinkText}
            </a>{' '}
            {t.ui.blogLinkSuffix}
          </p>
        </header>

        <div className={`${THEME.bg.secondary} rounded-lg overflow-hidden shadow-2xl`}>
          {/* Room visualization with white background */}
          <div className="h-64 bg-white flex items-center justify-center relative">
            <img
              src={room?.image}
              alt={room?.title}
              className="h-full object-contain max-w-full"
              onError={(e) => {
                // Fallback to placeholder if image doesn't exist
                e.target.style.display = 'none';
                const sibling = e.target.nextElementSibling;
                if (sibling) {
                  sibling.style.display = 'flex';
                }
              }}
            />
            {/* Fallback placeholder */}
            <div className="hidden items-center justify-center h-full w-full text-gray-500">
              <div className="text-center">
                <Database className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t.ui.roomLoading}</p>
              </div>
            </div>

            {/* Back button positioned at bottom left of image area */}
            {roomHistory.length > 1 && (
              <button
                onClick={goBack}
                disabled={isTransitioning}
                className={`absolute bottom-3 left-3 inline-flex items-center space-x-2 ${THEME.bg.tertiary} ${THEME.bg.hover} disabled:opacity-50 disabled:cursor-not-allowed ${THEME.text.secondary} px-3 py-2 rounded-lg text-sm transition-all duration-200 ${THEME.border.secondary} border ${THEME.border.hover} hover:${THEME.text.primary} shadow-lg`}
                aria-label={t.ui.backButton}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="sr-only">{t.ui.backButton}</span>
              </button>
            )}
          </div>

          {/* Room content */}
          <div className="p-6 md:p-8">
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${THEME.text.accent}`}>
              {room?.title}
            </h2>
            <p className={`${THEME.text.secondary} text-sm md:text-base leading-relaxed mb-6`}>
              {room?.description}
            </p>
            <p className={`${THEME.text.highlight} text-lg md:text-xl font-semibold mb-6`}>
              {room?.question}
            </p>

            {/* Choice options */}
            <div className="space-y-3 md:space-y-4">
              {room?.choices?.map((choice, index) => (
                <button
                  key={index}
                  onClick={() => handleChoice(choice)}
                  disabled={isTransitioning}
                  className={`w-full ${THEME.bg.tertiary} ${THEME.bg.hover} disabled:bg-slate-800 disabled:opacity-50 ${THEME.text.primary} p-3 md:p-4 rounded-lg text-left transition-all duration-200 ${THEME.border.secondary} border ${THEME.border.hover} shadow-lg hover:shadow-xl hover:scale-[1.02] transform`}
                >
                  <span className="text-sm md:text-base leading-relaxed">{choice.text}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}